import { devLog } from "utils";
import { IndexObjectType } from "components/dataGrid/LibraryDataGrip";
import { DEV_MODE } from "consts/env";
import { DropDownListOptionType } from "types/common/CommonType";
import { create } from "zustand";

export type MatchOptionType = "AUTOMATIC" | "CUSTOM" | "EXACT";
export type WordOrderType = "IGNORE" | "KEEP";
export type CaseSensitiveType = "IGNORE" | "KEEP";
export type WhiteSpaceType = "IGNORE" | "KEEP" | "SPLIT";
export type PunctuationType = "IGNORE" | "KEEP" | "SPLIT";

export interface SelectedConnectionsType {
  left?: DropDownListOptionType;
  right?: DropDownListOptionType;
}

export interface AttachDataType {
  description: string;
  lastModifiedDate: string;
  libraryName: string;
  libraryDetailId: string;
  libraryVersion: string;
  modifiedBy: string;
  rows: string;
  libraryId?: string;
  tagNameList?: string[];
}
interface AttachStoreType {
  attachDatasetData: AttachDataType;
  selectedDatasetID: string;
  lookupType: string;
  numberOfAttach: number;
  leftColumnList: DropDownListOptionType[]; // left 테이블의 컬럼 리스트
  rightColumnList: DropDownListOptionType[]; // right 테이블의 컬럼 리스트
  selectedLeftColumnList: DropDownListOptionType[];
  selectedRightColumnList: DropDownListOptionType[];
  selectedConnections: SelectedConnectionsType[];
  rangeSelectedColumn?: DropDownListOptionType;
  rangeFromColumn?: DropDownListOptionType;
  rangeToColumn?: DropDownListOptionType;
  rangeFromExclusive: boolean;
  rangeToExclusive: boolean;
  attachMatchOption: MatchOptionType;
  attachWordOrder: WordOrderType;
  attachCaseSensitive: CaseSensitiveType;
  attachWhiteSpace: WhiteSpaceType;
  attachPunctuation: PunctuationType;
  attachPunctuationList: string[];
  matchedColumns: IndexObjectType;
  unmatchedColumnsInDf1: IndexObjectType;
  unmatchedColumnsInDf2: IndexObjectType;
  selectedAttachDatasetId: string;
  setAttachDatasetData: (newData: Partial<AttachDataType>) => void;
  setSelectedDatasetID: (libraryID: string) => void;
  setLookupType: (selected: string) => void;
  setNumberOfAttach: (count: number) => void;
  setLeftColumnList: (list: DropDownListOptionType[]) => void;
  setRightColumnList: (list: DropDownListOptionType[]) => void;
  setSelectedLeftColumn: (
    column: DropDownListOptionType,
    index: number
  ) => void;
  setSelectedRightColumn: (
    column: DropDownListOptionType,
    index: number
  ) => void;
  setSelectedLeftColumnList: (list: DropDownListOptionType[]) => void;
  setSelectedRightColumnList: (list: DropDownListOptionType[]) => void;
  setSelectedConnections: (connections: SelectedConnectionsType[]) => void;
  addSelectedConnections: () => void;
  delSelectedConnections: (index: number) => void;
  setRangeSelectedColumn: (column?: DropDownListOptionType) => void;
  setRangeFromColumn: (column?: DropDownListOptionType) => void;
  setRangeToColumn: (column?: DropDownListOptionType) => void;
  setRangeFromExclusive: (value: boolean) => void;
  setRangeToExclusive: (value: boolean) => void;
  setAttachMatchOption: (value: MatchOptionType) => void;
  setAttachWordOrder: (value: WordOrderType) => void;
  setAttachCaseSensitive: (value: CaseSensitiveType) => void;
  setAttachWhiteSpace: (value: WhiteSpaceType) => void;
  setAttachPunctuation: (value: PunctuationType) => void;
  setAttachPunctuationList: (list: string[]) => void;
  setMatchedColumns: (matchColumn: IndexObjectType) => void;
  setUnmatchedColumnsInDf1: (columns: IndexObjectType) => void;
  setUnmatchedColumnsInDf2: (columns: IndexObjectType) => void;
  setSelectedAttachDatasetId: (libraryId: string) => void;
  resetAttachStore: () => void;
}

const useAttachStore = create<AttachStoreType>((set) => ({
  attachDatasetData: {
    description: "",
    libraryVersion: "",
    lastModifiedDate: "",
    libraryId: "",
    libraryDetailId: "",
    libraryName: "",
    modifiedBy: "",
    rows: "",
    tagNameList: [],
  },
  selectedDatasetID: "",
  selectedAttachDatasetId: "",
  lookupType: "LEFTOUTER",
  numberOfAttach: 0,
  leftColumnList: [],
  rightColumnList: [],
  selectedLeftColumnList: [],
  selectedRightColumnList: [],
  selectedConnections: [{}],
  rangeSelectedColumn: undefined,
  rangeFromColumn: undefined,
  rangeToColumn: undefined,
  rangeFromExclusive: true,
  rangeToExclusive: true,
  attachMatchOption: "EXACT",
  attachWordOrder: "IGNORE",
  attachCaseSensitive: "IGNORE",
  attachWhiteSpace: "SPLIT",
  attachPunctuation: "SPLIT",
  attachPunctuationList: [",", "-"],
  matchedColumns: {},
  unmatchedColumnsInDf1: {},
  unmatchedColumnsInDf2: {},
  setAttachDatasetData: (newData: Partial<AttachDataType>) => {
    set((state) => ({
      attachDatasetData: { ...state.attachDatasetData, ...newData },
    }));
  },
  setSelectedDatasetID: (libraryID: string) => {
    set({ selectedDatasetID: libraryID });
  },
  setLookupType: (selected: string) => {
    set({ lookupType: selected });
  },
  setNumberOfAttach: (count: number) => {
    set({ numberOfAttach: count });
  },
  setLeftColumnList: (list: DropDownListOptionType[]) => {
    set({ leftColumnList: list.filter((item) => item.value !== "$$_Sources") });
  },
  setRightColumnList: (list: DropDownListOptionType[]) => {
    set({
      rightColumnList: list.filter((item) => item.value !== "$$_Sources"),
    });
  },
  setSelectedLeftColumn: (column: DropDownListOptionType, index: number) => {
    set((state) => {
      let updatedConnections = [...state.selectedConnections];
      updatedConnections[index] = {
        ...updatedConnections[index],
        left: column,
      };
      return { selectedConnections: updatedConnections };
    });
  },
  setSelectedRightColumn: (column: DropDownListOptionType, index: number) => {
    set((state) => {
      let updatedConnections = [...state.selectedConnections];
      updatedConnections[index] = {
        ...updatedConnections[index],
        right: column,
      };
      return { selectedConnections: updatedConnections };
    });
  },
  setSelectedLeftColumnList: (list: DropDownListOptionType[]) => {
    set({ selectedLeftColumnList: list });
  },
  setSelectedRightColumnList: (list: DropDownListOptionType[]) => {
    set({ selectedRightColumnList: list });
  },
  setSelectedConnections: (connections: SelectedConnectionsType[]) => {
    set({ selectedConnections: connections });
  },
  addSelectedConnections: () => {
    set((state) => ({
      selectedConnections: [...state.selectedConnections, {}],
    }));
  },
  delSelectedConnections: (index: number) => {
    set((state) => {
      const updatedConnections = state.selectedConnections.filter(
        (item, idx) => index !== idx
      );
      return {
        selectedConnections: updatedConnections,
      };
    });
  },
  setRangeSelectedColumn: (column?: DropDownListOptionType) => {
    set({ rangeSelectedColumn: column });
  },
  setRangeFromColumn: (column?: DropDownListOptionType) => {
    set({ rangeFromColumn: column });
  },
  setRangeToColumn: (column?: DropDownListOptionType) => {
    set({ rangeToColumn: column });
  },
  setRangeFromExclusive: (value: boolean) => {
    set({ rangeFromExclusive: value });
  },
  setRangeToExclusive: (value: boolean) => {
    set({ rangeToExclusive: value });
  },
  setAttachMatchOption: (value: MatchOptionType) => {
    set({ attachMatchOption: value });
  },
  setAttachWordOrder: (value: WordOrderType) => {
    set({ attachWordOrder: value });
  },
  setAttachCaseSensitive: (value: CaseSensitiveType) => {
    set({ attachCaseSensitive: value });
  },
  setAttachWhiteSpace: (value: WhiteSpaceType) => {
    set({ attachWhiteSpace: value });
  },
  setAttachPunctuation: (value: PunctuationType) => {
    set({ attachPunctuation: value });
  },
  setMatchedColumns: (matchColumn: IndexObjectType) => {
    set({ matchedColumns: matchColumn });
  },
  setUnmatchedColumnsInDf1: (columns: IndexObjectType) => {
    set({ unmatchedColumnsInDf1: columns });
  },
  setUnmatchedColumnsInDf2: (columns: IndexObjectType) => {
    set({ unmatchedColumnsInDf2: columns });
  },
  setAttachPunctuationList: (list: string[]) => {
    set({ attachPunctuationList: list });
  },
  setSelectedAttachDatasetId: (libraryId: string) => {
    set({ selectedAttachDatasetId: libraryId });
  },
  resetAttachStore: () => {
    set({
      // attachDatasetData: {
      //   description: "",
      //   libraryVersion: "",
      //   lastModifiedDate: "",
      //   libraryId: "",
      //   libraryDetailId: "",
      //   libraryName: "",
      //   modifiedBy: "",
      //   rows: "",
      //   tagNameList: [],
      // },
      selectedDatasetID: "",
      lookupType: "LEFTOUTER",
      numberOfAttach: 0,
      leftColumnList: [],
      rightColumnList: [],
      selectedLeftColumnList: [],
      selectedRightColumnList: [],
      selectedConnections: [{}],
      rangeSelectedColumn: undefined,
      rangeFromColumn: undefined,
      rangeToColumn: undefined,
      rangeFromExclusive: true,
      rangeToExclusive: true,
      attachMatchOption: "EXACT",
      attachWordOrder: "IGNORE",
      attachCaseSensitive: "IGNORE",
      attachWhiteSpace: "SPLIT",
      attachPunctuation: "SPLIT",
      attachPunctuationList: [",", "-"],
      matchedColumns: {},
      unmatchedColumnsInDf1: {},
      unmatchedColumnsInDf2: {},
    });
  },
}));

export default useAttachStore;
